// The location state that opens Bulk Shell's New run drawer pre-filled
// ("Run again"), and the pasted-list parser. Pure, so `npm test` runs it.
import type { ShellJob, ShellJobDeviceFilter, ShellJobItem, ShellJobTarget } from './shell-job.service';

export interface BulkShellPrefill {
  command?: string;
  timeout_ms?: number;
  concurrency?: number;
  tenant_id?: string;
  target?: ShellJobTarget;
  filter?: ShellJobDeviceFilter;
  /** No-timeout runs: the slot release to keep. */
  slot_release_ms?: number;
  /** Target "group": the group to run on. */
  group_id?: string;
  /** Machine IDs, for target "list": the paste list takes machine IDs only. */
  devices?: string[];
  /** How many of `devices` did not answer last time: they may have run the command already. */
  no_answer?: number;
}

/**
 * "Run again" state for a run: the same command, timeout, pace, tenant and
 * devices — or only `only` (problem devices, or one device). Send-only and
 * the rollout are not carried over: the new run waits for answers, all at once.
 */
export function runAgainState(job: ShellJob, items: ShellJobItem[], only?: ShellJobItem[]): BulkShellPrefill {
  const base: BulkShellPrefill = {
    command: job.command,
    timeout_ms: job.timeout_ms,
    ...(job.slot_release_ms ? { slot_release_ms: job.slot_release_ms } : {}),
    concurrency: job.concurrency,
    tenant_id: job.tenant_id,
  };
  // A device never enrolled has no machine ID and could not run anyway.
  const machineIds = (list: ShellJobItem[]) => list.map((i) => i.machine_id).filter(Boolean);
  if (only) {
    return {
      ...base,
      target: 'list',
      devices: machineIds(only),
      no_answer: only.filter((i) => i.status === 'timeout').length,
    };
  }
  if (job.target === 'list') return { ...base, target: 'list', devices: machineIds(items) };
  if (job.target === 'filter') return { ...base, target: 'filter', filter: job.target_filter };
  if (job.target === 'group') {
    // A deleted group: run on the same devices rather than widen to the whole tenant.
    return job.target_group_id ? { ...base, target: 'group', group_id: job.target_group_id } : { ...base, target: 'list', devices: machineIds(items) };
  }
  return { ...base, target: 'all' };
}

/** Identifiers from pasted text: one per line, or split by commas, semicolons or spaces; duplicates dropped. */
export function parseDeviceList(text: string): string[] {
  return [...new Set(text.split(/[\s,;]+/).filter(Boolean))];
}
